//@flow
import {IconButton, makeStyles} from '@material-ui/core';
import React, {useEffect} from 'react';
import {useFormContext} from 'react-hook-form';
import {Add, CloseOutlined, Visibility} from '@material-ui/icons';

import FileTypeIcons from '../../../../../../../elements/FileTypeIcons';
import {colors} from '../../../../../../../assets/styles/colors';
import ImageRenderer from '../../../../../../../elements/ImageRenderer';
import CustomHorizontalScroll from '../../../../../../../elements/CustomHorizontalScroll';
import {TRIGGER_FIELDS} from '../../const';
import {useAlert} from '../../../../../../../elements/Alert/AlertContext';
import {getTriggerAttachmentUrl} from '../../../../utils';

const useStyles = makeStyles(() => ({
  attachmentFiles: {
    display: 'flex',
    gap: '.5rem',
    padding: '.5rem',
    background: 'white',
    borderRadius: '.5rem .5rem 0 0',
    overflowX: 'auto',
    width: '100%',
  },
  attachmentPreview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '4rem',
    height: '4rem',
    minWidth: '4rem',
    minHeight: '4rem',
    borderRadius: '.5rem',
    overflow: 'hidden',
    background: colors.grey200,
    fontSize: '1.25rem',
    flexDirection: 'column',
    gap: '.25rem',

    '&[data-document]': {
      minWidth: '6rem',
      paddingTop: '.25rem',
      width: '6rem',
    },

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },

    '&:hover $actionsOverlay': {
      opacity: 1,
    },
  },
  attachmentPreviewName: {
    fontSize: '.75rem',
    textAlign: 'center',
    width: '95%',
    display: '-webkit-box',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  actionsOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '.5rem',
    opacity: 0,
    transition: 'opacity 0.2s',
    zIndex: 2,
    borderRadius: '.5rem',
  },
  actionButton: {
    color: 'white',
    padding: 4,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
    },
  },
  addAttachment: {
    width: '4rem',
    height: '4rem',
    minWidth: '4rem',
    minHeight: '4rem',
    padding: '0',
    borderRadius: '.5rem',
    background: colors.grey200,

    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const maxSize = 1024 * 1024 * 1;

export default function Attachments() {
  const classes = useStyles();

  const {watch, setValue, register} = useFormContext();
  const {showAlert} = useAlert();

  useEffect(() => {
    register(TRIGGER_FIELDS.attachments);
  }, [register]);

  const files = watch(TRIGGER_FIELDS.attachments, []);
  const savedDocuments = watch(TRIGGER_FIELDS.savedDocuments, []);
  const savedImages = watch(TRIGGER_FIELDS.savedImages, []);
  const savedVideos = watch(TRIGGER_FIELDS.savedVideos, []);

  const handleRemoveSavedFile = (
    url: string,
    type: 'document' | 'image' | 'video'
  ) => {
    if (type === 'image') {
      const newImages = savedImages.filter(img => img !== url);
      setValue(TRIGGER_FIELDS.savedImages, newImages);
      return;
    }

    if (type === 'video') {
      const newVideos = savedVideos.filter(vid => vid !== url);
      setValue(TRIGGER_FIELDS.savedVideos, newVideos);
      return;
    }

    if (type === 'document') {
      const newDocuments = savedDocuments.filter(doc => doc !== url);
      setValue(TRIGGER_FIELDS.savedDocuments, newDocuments);
      return;
    }
  };

  const handleRemove = (file: File) => {
    const newFiles = files.filter(f => f.name !== file.name);
    setValue(TRIGGER_FIELDS.attachments, newFiles);
  };

  const handleAddAttachment = (uploadedFiles: File[]) => {
    const uniqueUploadedFiles = uploadedFiles.filter(file => {
      const unique = !files.find(f => f.name === file.name);
      const withCorrectSize = file.size < maxSize;

      if (!withCorrectSize) {
        showAlert(`El archivo no puede ser mayor a 1MB, (${file.name})`, {
          type: 'warning',
        });
      }

      return unique && withCorrectSize;
    });

    const maxFiles = 10 - files.length;

    const slicedUploadedFiles = uniqueUploadedFiles.slice(0, maxFiles);

    const newFiles = [...files, ...slicedUploadedFiles];

    setValue(TRIGGER_FIELDS.attachments, newFiles);
  };

  const totalLength =
    files.length +
    savedDocuments.length +
    savedImages.length +
    savedVideos.length;

  const showAddButton = totalLength < 10;

  return (
    <CustomHorizontalScroll
      classes={{
        scrollContainer: classes.attachmentFiles,
      }}
    >
      {savedImages.map((image, index) => (
        <AttachmentFileItem
          key={`${image}-${index}}`}
          src={getTriggerAttachmentUrl(image)}
          name={image}
          type="image"
          onRemove={() => {
            handleRemoveSavedFile(image, 'image');
          }}
        />
      ))}

      {savedVideos.map((video, index) => (
        <AttachmentFileItem
          key={`${video}-${index}}`}
          src={getTriggerAttachmentUrl(video)}
          name={video}
          type="video"
          onRemove={() => {
            handleRemoveSavedFile(video, 'video');
          }}
        />
      ))}

      {savedDocuments.map((document, index) => (
        <AttachmentFileItem
          key={`${document}-${index}}`}
          src={getTriggerAttachmentUrl(document)}
          name={document}
          type="document"
          onRemove={() => {
            handleRemoveSavedFile(document, 'document');
          }}
        />
      ))}

      {files.map((file, index) => (
        <AttachmentFileItem
          key={`${file.name}-${index}}`}
          src={file}
          name={file.name}
          type={file.type}
          onRemove={() => {
            handleRemove(file);
          }}
        />
      ))}
      {showAddButton && (
        <label className={classes.addAttachment}>
          <Add />
          <input
            type="file"
            hidden
            multiple
            onInput={e => {
              handleAddAttachment(Array.from(e.target.files));
              e.target.value = null;
            }}
          />
        </label>
      )}
    </CustomHorizontalScroll>
  );
}

type AttachmentFileItemProps = {
  src: File | string,
  name: string,
  type: string,
  onRemove: () => void,
};
function AttachmentFileItem(props: AttachmentFileItemProps) {
  const classes = useStyles();

  const {src, onRemove, type, name} = props;
  const [previewUrl, setPreviewUrl] = React.useState('');

  React.useEffect(() => {
    if (src instanceof File) {
      const url = URL.createObjectURL(src);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (typeof src === 'string') {
      setPreviewUrl(src);
    }
  }, [src]);

  const isImage = type.includes('image');

  if (isImage) {
    return (
      <picture className={classes.attachmentPreview}>
        <div className={classes.actionsOverlay}>
          <IconButton
            className={classes.actionButton}
            size="small"
            onClick={onRemove}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        </div>

        <ImageRenderer src={src} alt={name} />
      </picture>
    );
  }

  return (
    <div className={classes.attachmentPreview} data-document>
      <div className={classes.actionsOverlay}>
        {previewUrl && (
          <IconButton
            className={classes.actionButton}
            size="small"
            component="a"
            href={previewUrl}
            target="_blank"
            title="Ver archivo"
          >
            <Visibility fontSize="small" />
          </IconButton>
        )}
        <IconButton
          className={classes.actionButton}
          size="small"
          onClick={onRemove}
          title="Eliminar"
        >
          <CloseOutlined fontSize="small" />
        </IconButton>
      </div>

      <FileTypeIcons type={type} />

      <span className={classes.attachmentPreviewName}>{name}</span>
    </div>
  );
}
